import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Edit, Trash2, AlertCircle, Clock, GripVertical } from "lucide-react";
import { GiChiliPepper } from "react-icons/gi";

import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { MenuItem } from "@/model/entities";
import ConfirmationDialog from "./ConfirmationDialog";
import useAppStore from "@/store/appStore";
import { getFirebaseImageUrl } from "@/service/firebaseService";

export interface MenuItemCardProps {
  menuItem: MenuItem;
  onEdit: (menuItem: MenuItem) => void;
  onDelete: (menuItem: MenuItem) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
}

const MenuItemCard: React.FC<MenuItemCardProps> = ({
  menuItem,
  onEdit,
  onDelete,
  dragHandleProps,
}) => {
  const { t } = useTranslation();
  const language = useAppStore((state) => state.language);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [defaultImageUrl, setDefaultImageUrl] = useState<string>("");
  const isRTL = language === "he";

  useEffect(() => {
    getFirebaseImageUrl("static/default-menu-item.webp").then(
      setDefaultImageUrl
    );
  }, []);

  const handleDelete = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const confirmDelete = useCallback(() => {
    onDelete(menuItem);
    setShowDeleteConfirm(false);
  }, [onDelete, menuItem]);

  const formattedPrice = useMemo(() => {
    return new Intl.NumberFormat(language === "he" ? "he-IL" : "en-US", {
      style: "currency",
      currency: "ILS",
    }).format(menuItem.price);
  }, [menuItem, language]);

  return (
    <>
      <Card
        className="overflow-hidden hover:shadow-lg transition-shadow py-0"
        data-testid="menu-item"
      >
        <div className="flex flex-col sm:flex-row">
          <div className="relative w-full h-[200px] sm:h-[192px] sm:max-w-[192px] flex-shrink-0">
            <div
              className="absolute top-0 start-0 h-full flex items-center pe-2"
              {...dragHandleProps}
            >
              <GripVertical className="h-5 w-5 text-muted-foreground cursor-grab" />
            </div>
            {!defaultImageUrl ? (
              <Skeleton className="w-full h-full object-cover rounded-md" />
            ) : (
              <img
                src={menuItem.imageUrl ?? defaultImageUrl}
                alt={menuItem.name}
                className="w-full h-full object-cover rounded-md"
              />
            )}
          </div>

          <div className="flex-1 p-4 flex flex-col justify-between gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-base sm:text-lg leading-tight truncate">
                  {menuItem.name}
                </h3>
                <span className="font-bold text-orange-500 text-sm">
                  {formattedPrice}
                </span>
              </div>
              <p
                className={cn(
                  "text-sm text-muted-foreground line-clamp-2",
                  isRTL ? "text-right" : "text-left"
                )}
              >
                {menuItem.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {!menuItem.isAvailable && (
                <Badge
                  variant="outline"
                  className="border-red-500 text-red-500"
                >
                  <AlertCircle className="h-3 w-3 me-1" />
                  {t("menuItem.unavailable", "לא זמין")}
                </Badge>
              )}

              {typeof menuItem.spicyLevel === "number" && (
                <Badge
                  variant="outline"
                  className="border-orange-500 text-orange-500 flex items-center"
                >
                  {/* Render 3 flame icons; fill depends on spicyLevel (0-3) */}
                  <span className="flex items-center me-1" aria-hidden>
                    {Array.from({ length: 3 }).map((_, idx) => {
                      const active = idx < (menuItem.spicyLevel ?? 0);
                      return (
                        <GiChiliPepper
                          key={idx}
                          className={cn(
                            "h-3 w-3",
                            "me-0.5",
                            active
                              ? "text-orange-500"
                              : "text-muted-foreground/60"
                          )}
                        />
                      );
                    })}
                  </span>
                  {/* Accessible label for screen readers */}
                  <span className="sr-only">
                    {t(`menuItemForm.spicyLevels.${menuItem.spicyLevel}`)}
                  </span>
                </Badge>
              )}

              {menuItem.allergens && (
                <Badge
                  variant="outline"
                  className="border-red-500 text-red-500"
                >
                  <AlertCircle className="h-3 w-3 me-1" />
                  {menuItem.allergens}
                </Badge>
              )}

              {menuItem.preparationTime && (
                <Badge variant="outline">
                  <Clock className="h-3 w-3 me-1" />
                  {menuItem.preparationTime} {t("menuItem.minutes")}
                </Badge>
              )}
            </div>

            <div className="flex gap-2 self-end">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(menuItem)}
                className="text-muted-foreground hover:text-primary"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDelete}
                className="text-red-500 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default React.memo(MenuItemCard);
